import { createContext, PropsWithChildren, useEffect, useState } from "react"
import { Cart, MainProduct, MainProductVariant, Screen } from "../../types"
import { areArraysEqual, getActiveMaterialByMainProductVariantId, getInitialActivePendantProductVariantIds, isEditMode, isPos, loadActivePendantsFromCookies, saveActivePendantsToCookies } from "../../utils"
import { getCorrespondingPendantProductVariantIdsByMaterial, getInitialEditProductVariantIds, getInitialUrlQueryProductVariantIds, updateUrlQueryProductVariantIds } from "./utils"

type AppContext = {
  mainProduct: MainProduct
  bufferedMainProduct: MainProduct | null
  setBufferedMainProduct: (product: MainProduct | null) => void
  bufferedMainProductVariant: MainProductVariant | null
  setBufferedMainProductVariant: (variant: MainProductVariant | null) => void
  scrollLeft?: number
  linkedMainProducts: MainProduct[]
  cart: Cart
  activeScreen: Screen
  setActiveScreen: (screen: Screen) => void
  activeMainProductVariantId: string,
  setActiveMainProductVariantId: (id: string) => void
  activePendantProductVaraintIds: string[]
  setActivePendantProductVaraintIds: (id: string[]) => void
  active: number
  setActive: (index: number) => void
  activePendantProductId: string
  setActivePendantProductId: (activePendantProductId: string) => void
  designSummaryPopupOpen: boolean
  setDesignSummaryPopupOpen: (designSummaryPopupOpen: boolean) => void
  confirmationPopupDisplayed: boolean,
  setConfirmationPopupDisplayed: (displayed: boolean) => void
  redirectingToNextPage: boolean
  setRedirectingToNextPage: (redirecting: boolean) => void
  reset: () => void,
  isMainProductSinglePendant: boolean
}

const initialValue: AppContext = {
  mainProduct: {
    id: 0,
    title: "",
    tags: [],
    collectionUrl: "",
    url: "",
    variants: [],
    optionsWithValues: [],
    metafields: {
      type: "Necklace",
      pdpBiLine: "",
      pendantProducts: [],
      dragAndDropEnabled: false
    },
    globalSettings: {
      dragAndDropEnabled: false
    },
    isMainProductSinglePendant: false
  },
  bufferedMainProduct: null,
  setBufferedMainProduct: () => null,
  bufferedMainProductVariant: null,
  setBufferedMainProductVariant: () => null,
  linkedMainProducts: [],
  cart: { items: [] },
  activeScreen: 'CHAIN', 
  setActiveScreen: () => null,
  activeMainProductVariantId: "",
  setActiveMainProductVariantId: () => null,
  activePendantProductVaraintIds: [],
  setActivePendantProductVaraintIds: () => null,
  active: 0,
  setActive: () => null,
  activePendantProductId: "",
  setActivePendantProductId: () => null,
  designSummaryPopupOpen: false,
  setDesignSummaryPopupOpen: () => null,
  reset: () => null,
  confirmationPopupDisplayed: false,
  setConfirmationPopupDisplayed: () => null,
  redirectingToNextPage: false,
  setRedirectingToNextPage: () => null,
}

export const AppContext = createContext<AppContext>(initialValue)

type Props = PropsWithChildren & {
  mainProduct: MainProduct
  linkedMainProducts: MainProduct[]
  cart: Cart
  remounted?: boolean
}

export const AppProvider = ({ children, mainProduct, linkedMainProducts, cart, remounted }: Props) => {
  const initialUrlQueryProductVariantIds = getInitialUrlQueryProductVariantIds(mainProduct)
  const initialEditProductVariantIds = isEditMode() && !remounted ? getInitialEditProductVariantIds(mainProduct, cart) : {}

  const isMainProductSinglePendant = mainProduct.metafields.builderData.maxNumberOfPendants === 1

  const initialActiveMainProductVariantId = mainProduct.variants[0].id

  const initialActivePendantProductId = mainProduct.metafields.pendantProducts[0].id
  const initialPendantVariantIds = getInitialActivePendantProductVariantIds(mainProduct, initialActiveMainProductVariantId)  

  const userPendantVariantIds = getInitialActivePendantProductVariantIds(mainProduct, initialActiveMainProductVariantId)
  const posStoragePendantVariantIds = loadActivePendantsFromCookies(mainProduct.id)?.pendantIds ?? userPendantVariantIds
  const posStorageMainProductVariantId = loadActivePendantsFromCookies(mainProduct.id)?.chainId ?? mainProduct.variants[0].id 

  const [activeScreen, setActiveScreen] = useState<Screen>(remounted ? 'CHAIN' : 'PENDANTS')
  const [activeMainProductVariantId, setActiveMainProductVariantId] = useState(
    isEditMode() && !remounted
      ? initialEditProductVariantIds!.chain
      : isPos() 
        ? posStorageMainProductVariantId 
        : initialUrlQueryProductVariantIds
          ? initialUrlQueryProductVariantIds.chain
          : initialActiveMainProductVariantId
  )
  const [activePendantProductId, setActivePendantProductId] = useState(initialActivePendantProductId)
  const _activePendantProductVaraintIds = isEditMode() && !remounted
    ? initialEditProductVariantIds!.pendants
    : isPos() 
      ? posStoragePendantVariantIds 
      : initialUrlQueryProductVariantIds
        ? initialUrlQueryProductVariantIds.pendants
        : userPendantVariantIds

  const [activePendantProductVaraintIds, setActivePendantProductVaraintIds] = useState<number[]>(
    _activePendantProductVaraintIds
  )

  const initialSelectedPendantProductIndex = _activePendantProductVaraintIds.length - 1

  const [selectedPendantProductIndex, setSelectedPendantProductIndex] = useState<number>(initialSelectedPendantProductIndex)
  const [designSummaryPopupOpen, setDesignSummaryPopupOpen] = useState(false)

  const [confirmationPopupDisplayed, setConfirmationPopupDisplayed] = useState<boolean>(false)
  const [redirectingToNextPage, setRedirectingToNextPage] = useState(false)
  const [bufferedMainProduct, setBufferedMainProduct] = useState<MainProduct | null>(null)
  const [bufferedMainProductVariant, setBufferedMainProductVariant] = useState<MainProductVariant | null>(null)

  const reset = () => {
    setSelectedPendantProductIndex(initialSelectedPendantProductIndex)
    setActiveMainProductVariantId(initialActiveMainProductVariantId)
    setActivePendantProductVaraintIds(initialPendantVariantIds)
    setActiveScreen('PENDANTS')
  }

  useEffect(() => {
    const activeMaterial = getActiveMaterialByMainProductVariantId(mainProduct, activeMainProductVariantId)

    const correspondingPendantProductVariantIdsByMaterial = getCorrespondingPendantProductVariantIdsByMaterial(
      mainProduct, 
      activePendantProductVaraintIds, 
      activeMaterial
    )

    const pendantVariantIdsWillChange = !areArraysEqual(correspondingPendantProductVariantIdsByMaterial, activePendantProductVaraintIds)
    updateUrlQueryProductVariantIds(activeMainProductVariantId, activePendantProductVaraintIds)
    
    if (pendantVariantIdsWillChange) {
      setActivePendantProductVaraintIds(correspondingPendantProductVariantIdsByMaterial)
    }
  }, [activeMainProductVariantId])

  useEffect(() => {
    !isPos() && updateUrlQueryProductVariantIds(activeMainProductVariantId, activePendantProductVaraintIds)
    isPos() && saveActivePendantsToCookies(mainProduct.id, activeMainProductVariantId, activePendantProductVaraintIds)
  }, [activePendantProductVaraintIds])

  return (
    <AppContext.Provider value={{
      mainProduct,
      isMainProductSinglePendant,
      bufferedMainProduct,
      setBufferedMainProduct,
      bufferedMainProductVariant,
      setBufferedMainProductVariant,
      linkedMainProducts,
      cart,
      activeScreen,
      setActiveScreen,
      activeMainProductVariantId,
      setActiveMainProductVariantId,
      activePendantProductVaraintIds,
      setActivePendantProductVaraintIds,
      active: selectedPendantProductIndex,
      setActive: setSelectedPendantProductIndex,
      activePendantProductId,
      setActivePendantProductId,
      designSummaryPopupOpen,
      setDesignSummaryPopupOpen,
      reset,
      confirmationPopupDisplayed,
      setConfirmationPopupDisplayed,
      redirectingToNextPage,
      setRedirectingToNextPage,
    }}>
      { children }
    </AppContext.Provider>
  )
}
